import React from 'react';
import { TicketStatus, TicketSeverity } from '../context/TicketContext';

const STATUS_META: Record<TicketStatus, { label: string; color: string; icon: string }> = {
  open:        { label: 'Open',        color: '#f87171', icon: '🔴' },
  assigned:    { label: 'Assigned',    color: '#fbbf24', icon: '🟡' },
  in_progress: { label: 'In Progress', color: '#60a5fa', icon: '🔵' },
  resolved:    { label: 'Resolved',    color: '#34d399', icon: '🟢' },
  closed:      { label: 'Closed',      color: '#6b7280', icon: '⚫' },
};

const SEV_META: Record<TicketSeverity, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  warning:  { label: 'Warning',  color: '#fbbf24', bg: 'rgba(251,191,36,0.12)'  },
};

interface StatusBadgeProps {
  status: TicketStatus;
  size?: 'sm' | 'md';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'md' }) => {
  const m = STATUS_META[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: size === 'sm' ? '2px 8px' : '4px 10px',
      borderRadius: 20, fontSize: size === 'sm' ? 11 : 12, fontWeight: 600,
      color: m.color, background: `${m.color}18`, border: `1px solid ${m.color}44`,
    }}>
      {m.icon} {m.label}
    </span>
  );
};

interface SeverityBadgeProps {
  severity: TicketSeverity;
  size?: 'sm' | 'md';
}

export const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity, size = 'md' }) => {
  const m = SEV_META[severity];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: size === 'sm' ? '2px 8px' : '4px 10px',
      borderRadius: 20, fontSize: size === 'sm' ? 11 : 12, fontWeight: 600,
      color: m.color, background: m.bg, border: `1px solid ${m.color}44`,
    }}>
      {severity === 'critical' ? '🔴' : '🟡'} {m.label}
    </span>
  );
};

interface SlaCountdownProps {
  deadline: number;
  resolved?: boolean;
}

export const SlaCountdown: React.FC<SlaCountdownProps> = ({ deadline, resolved }) => {
  const now       = Date.now();
  const remaining = deadline - now;
  const overdue   = remaining < 0;

  if (resolved) return <span style={{ color: '#34d399', fontSize: 12 }}>✅ Within SLA</span>;

  const abs  = Math.abs(remaining);
  const hrs  = Math.floor(abs / 3600_000);
  const mins = Math.floor((abs % 3600_000) / 60_000);
  const label = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  const color = overdue ? '#f87171' : remaining < 3600_000 ? '#fbbf24' : '#34d399';

  return (
    <span style={{ color, fontSize: 12, fontWeight: 600 }}>
      {overdue ? `⚠️ Overdue by ${label}` : `⏱ ${label} remaining`}
    </span>
  );
};
